/**
 * FireworksEffect.jsx
 * Animated fireworks effect for New Year's Eve celebration
 */
import React, { useEffect, useState, useCallback } from 'react';

const FireworksEffect = ({ intensity = 'medium', enabled = true }) => {
  const [fireworks, setFireworks] = useState([]);
  const [particleKey, setParticleKey] = useState(0);

  const intensityConfig = {
    light: { interval: 2500, particleCount: 15 },
    medium: { interval: 1800, particleCount: 25 },
    heavy: { interval: 1200, particleCount: 35 }
  };

  const colors = [
    ['#FFD700', '#FFA500'], // Gold to Orange
    ['#FF6B6B', '#FF1744'], // Red
    ['#4ECDC4', '#00BCD4'], // Teal
    ['#E040FB', '#9C27B0'], // Purple
    ['#69F0AE', '#00E676'], // Green
    ['#40C4FF', '#2196F3'], // Blue
    ['#FFFF00', '#FFEB3B'], // Yellow
    ['#FF4081', '#F50057'], // Pink
  ];

  const config = intensityConfig[intensity] || intensityConfig.medium;

  const createFirework = useCallback(() => {
    const colorPair = colors[Math.floor(Math.random() * colors.length)];
    const x = 15 + Math.random() * 70; // 15-85% from left
    const y = 20 + Math.random() * 40; // 20-60% from top
    
    const particles = Array.from({ length: config.particleCount }, (_, i) => {
      const angle = (i / config.particleCount) * Math.PI * 2;
      const velocity = 2 + Math.random() * 3;
      const size = 2 + Math.random() * 3;
      
      return {
        id: `${Date.now()}-${i}`,
        angle,
        velocity,
        size,
        color: Math.random() > 0.5 ? colorPair[0] : colorPair[1],
        delay: Math.random() * 0.1
      };
    });

    return {
      id: Date.now(),
      x,
      y,
      particles,
      createdAt: Date.now()
    };
  }, [config.particleCount]);

  useEffect(() => {
    if (!enabled) {
      setFireworks([]);
      return;
    }

    // Create initial firework
    setFireworks([createFirework()]);

    // Create new fireworks at interval
    const interval = setInterval(() => {
      setFireworks(prev => {
        // Remove old fireworks (older than 2 seconds)
        const now = Date.now();
        const active = prev.filter(fw => now - fw.createdAt < 2000);
        
        // Add new firework
        return [...active, createFirework()];
      });
      setParticleKey(k => k + 1);
    }, config.interval);

    return () => clearInterval(interval);
  }, [enabled, config.interval, createFirework]);

  if (!enabled) return null;

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden z-[1]">
      <style>{`
        @keyframes fireworkLaunch {
          0% {
            transform: translateY(100%);
            opacity: 1;
          }
          100% {
            transform: translateY(0);
            opacity: 0;
          }
        }
        
        @keyframes fireworkExplode {
          0% {
            transform: scale(0) translate(0, 0);
            opacity: 1;
          }
          50% {
            opacity: 1;
          }
          100% {
            transform: scale(1) translate(var(--tx), var(--ty));
            opacity: 0;
          }
        }
        
        @keyframes fireworkGlow {
          0%, 100% {
            filter: brightness(1);
          }
          50% {
            filter: brightness(1.5);
          }
        }
        
        .firework-particle {
          position: absolute;
          border-radius: 50%;
          animation: fireworkExplode 1.5s ease-out forwards, fireworkGlow 0.3s ease-in-out infinite;
        }
        
        .firework-center {
          position: absolute;
          width: 8px;
          height: 8px;
          background: white;
          border-radius: 50%;
          animation: fireworkGlow 0.2s ease-in-out infinite;
          box-shadow: 0 0 20px 10px rgba(255, 255, 255, 0.8);
        }
      `}</style>
      
      {fireworks.map(firework => (
        <div
          key={firework.id}
          className="absolute"
          style={{
            left: `${firework.x}%`,
            top: `${firework.y}%`,
            transform: 'translate(-50%, -50%)'
          }}
        >
          {/* Center flash */}
          <div className="firework-center" />
          
          {/* Particles */}
          {firework.particles.map(particle => {
            const tx = Math.cos(particle.angle) * particle.velocity * 40;
            const ty = Math.sin(particle.angle) * particle.velocity * 40;
            
            return (
              <div
                key={particle.id}
                className="firework-particle"
                style={{
                  width: `${particle.size}px`,
                  height: `${particle.size}px`,
                  backgroundColor: particle.color,
                  boxShadow: `0 0 ${particle.size * 2}px ${particle.color}`,
                  '--tx': `${tx}px`,
                  '--ty': `${ty}px`,
                  animationDelay: `${particle.delay}s`
                }}
              />
            );
          })}
        </div>
      ))}
    </div>
  );
};

export default FireworksEffect;
